import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Twitter, Youtube } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const Footer = () => {
  const { t, lang } = useLang();
  const [settings, setSettings] = useState({});
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const onSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success(t.footer.subscribeOk);
    setEmail("");
  };

  return (
    <footer style={{ borderTop: "1px solid rgba(139,32,53,0.25)", background: "var(--rich-black)", color: "var(--warm-off-white)", padding: "80px clamp(24px, 6vw, 96px) 32px" }} data-testid="main-footer">
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 48 }}>
        <div>
          <Link to="/" className="font-display" style={{ fontSize: "2rem", fontWeight: 300, color: "var(--warm-off-white)", textDecoration: "none" }}>
            Larcraft<span style={{ color: "var(--gold)" }}>.</span>
          </Link>
          <p className="font-body mt-4" style={{ color: "rgba(248,244,240,0.65)", maxWidth: 320, lineHeight: 1.7, marginTop: 16 }}>
            {t.footer.tagline}
          </p>
          <div className="flex" style={{ gap: 14, marginTop: 24 }}>
            {settings.instagram && <a href={settings.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" style={{ color: "rgba(248,244,240,0.6)" }}><Instagram size={18} /></a>}
            {settings.linkedin && <a href={settings.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" style={{ color: "rgba(248,244,240,0.6)" }}><Linkedin size={18} /></a>}
            {settings.twitter && <a href={settings.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter" style={{ color: "rgba(248,244,240,0.6)" }}><Twitter size={18} /></a>}
            {settings.youtube && <a href={settings.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" style={{ color: "rgba(248,244,240,0.6)" }}><Youtube size={18} /></a>}
          </div>
        </div>

        <div>
          <div className="font-mono mb-5" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 20 }}>{t.footer.explore}</div>
          <ul style={{ display: "flex", flexDirection: "column", gap: 12, listStyle: "none", padding: 0 }}>
            <li><Link to="/services" className="link-underline" style={{ color: "rgba(248,244,240,0.75)", textDecoration: "none" }}>{t.nav.services}</Link></li>
            <li><Link to="/portfolio" className="link-underline" style={{ color: "rgba(248,244,240,0.75)", textDecoration: "none" }}>{t.nav.work}</Link></li>
            <li><Link to="/about" className="link-underline" style={{ color: "rgba(248,244,240,0.75)", textDecoration: "none" }}>{t.nav.about}</Link></li>
            <li><Link to="/blog" className="link-underline" style={{ color: "rgba(248,244,240,0.75)", textDecoration: "none" }}>{t.nav.blog}</Link></li>
            <li><Link to="/pricing" className="link-underline" style={{ color: "rgba(248,244,240,0.75)", textDecoration: "none" }}>{t.nav.pricing}</Link></li>
            <li><Link to="/contact" className="link-underline" style={{ color: "rgba(248,244,240,0.75)", textDecoration: "none" }}>{t.nav.contact}</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-mono mb-5" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 20 }}>{t.contact.info}</div>
          <ul style={{ display: "flex", flexDirection: "column", gap: 10, listStyle: "none", padding: 0, color: "rgba(248,244,240,0.75)" }}>
            <li>{settings.contact_email || "info@larcraftmedya.com"}</li>
            <li>{settings.contact_phone}</li>
            <li>{lang === "tr" ? settings.address_tr : settings.address_en}</li>
            <li style={{ color: "rgba(248,244,240,0.5)", fontSize: "0.85rem", marginTop: 8 }}>{lang === "tr" ? settings.working_hours_tr : settings.working_hours_en}</li>
          </ul>
        </div>

        <div>
          <div className="font-mono mb-5" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 20 }}>Newsletter</div>
          <p style={{ color: "rgba(248,244,240,0.65)", marginBottom: 16 }}>{t.footer.newsletter}</p>
          <form onSubmit={onSubscribe} data-testid="newsletter-form" style={{ display: "flex", gap: 8 }}>
            <input
              data-testid="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@..."
              className="input-line"
              style={{ flex: 1 }}
            />
            <button data-testid="newsletter-submit" type="submit" className="font-mono" style={{ padding: "8px 16px", background: "var(--bordeaux)", color: "var(--warm-off-white)", border: "none", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: "0.7rem", cursor: "pointer" }}>
              {t.footer.subscribe}
            </button>
          </form>
        </div>
      </div>

      <div style={{ marginTop: 64, paddingTop: 24, borderTop: "1px solid rgba(248,244,240,0.08)", display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 16, color: "rgba(248,244,240,0.45)", fontSize: "0.8rem" }}>
        <span>{t.footer.rights}</span>
        <div style={{ display: "flex", gap: 24 }}>
          <a href="#" style={{ color: "rgba(248,244,240,0.45)", textDecoration: "none" }}>{t.footer.privacy}</a>
          <a href="#" style={{ color: "rgba(248,244,240,0.45)", textDecoration: "none" }}>{t.footer.terms}</a>
          <Link to="/admin/login" data-testid="admin-link-footer" style={{ color: "rgba(248,244,240,0.45)", textDecoration: "none" }}>Admin</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
